/** Map file MIME type prefixes to human-readable labels */
const FILE_TYPE_LABELS: [RegExp, string][] = [
  [/^image\//, "📷 Bild"],
  [/^video\//, "🎬 Video"],
  [/^audio\//, "🎵 Sprachnachricht"],
  [/^application\/pdf/, "📄 PDF"],
  [/^application\//, "📎 Datei"],
];

/** Replace raw [file:id:mime:name] and [efile:id:mime:name:key:iv] tokens with readable labels */
export function humanizeFileTokens(text: string): string {
  // Handle encrypted file tokens [efile:id:mime:name:key:iv]
  let result = text.replace(/\[efile:[a-f0-9-]+:([^:]+):[^:]+:[A-Za-z0-9_-]+:[A-Za-z0-9_-]+\]/gi, (_match, mime: string) => {
    for (const [pattern, label] of FILE_TYPE_LABELS) {
      if (pattern.test(mime)) return label;
    }
    return "📎 Datei";
  });
  // Handle legacy unencrypted file tokens [file:id:mime:name]
  result = result.replace(/\[file:[a-f0-9-]+:([^:]+):[^\]]*\]/gi, (_match, mime: string) => {
    for (const [pattern, label] of FILE_TYPE_LABELS) {
      if (pattern.test(mime)) return label;
    }
    return "📎 Datei";
  });
  return result;
}

/** Strip inline markdown for sidebar previews (bold, italic, strikethrough, code) */
export function stripMarkdownForPreview(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, "📝 Code")   // code blocks
    .replace(/`([^`]+)`/g, "$1")              // inline code
    .replace(/\*\*\*(.+?)\*\*\*/g, "$1")     // bold+italic
    .replace(/\*\*(.+?)\*\*/g, "$1")         // bold
    .replace(/\*(.+?)\*/g, "$1")             // italic
    .replace(/~~(.+?)~~/g, "$1")             // strikethrough
    .replace(/^#{1,6}\s+/gm, "");            // headings
}

/**
 * Canonical set of bracketed status placeholders that stand in for a message
 * while decryption is pending or has failed. These are NOT real decrypted
 * content — never let one overwrite already-decrypted plaintext in a merge.
 */
export const DECRYPT_PLACEHOLDERS = new Set<string>([
  "[Nachricht wird entschlüsselt…]",
  "[Verschlüsselung wird initialisiert…]",
  "[Nachricht konnte nicht entschlüsselt werden]",
  "[Verschlüsselte Nachricht]",
  "[Eigene Nachricht – Verschlüsselung aktiv]",
  "[encrypted message]",
]);

/**
 * IDB plaintext-cache sentinel values ("__NO_BACKUP__", "__NO_FAILSAFE__:<ms>",
 * "__OWN_MISS__:<ms>") are negative-cache markers, never displayable content.
 */
export function isCacheSentinel(text: string): boolean {
  return (
    text === "__NO_BACKUP__" ||
    text.startsWith("__NO_FAILSAFE__:") ||
    text.startsWith("__OWN_MISS__:")
  );
}

/** true when `text` is real decrypted content (a non-empty, non-placeholder, non-sentinel string). */
export function isRealPlaintext(text?: string | null): boolean {
  return !!text && !DECRYPT_PLACEHOLDERS.has(text) && !isCacheSentinel(text);
}

/**
 * Safely extract display text from a message.
 * Never returns raw E2EE ciphertext (JSON envelopes) to the UI.
 */
export function safeMessageText(msg: { plaintext?: string | null; ciphertext?: string | null; encrypted?: boolean; contentType?: string }, fallback = ""): string {
  // If we have decrypted plaintext, use it
  if (msg.plaintext) return msg.plaintext;

  // If we have ciphertext, check if it's a raw E2EE envelope that should NOT be displayed
  if (msg.ciphertext) {
    // Detect E2EE envelopes: JSON starting with { containing "v" key (envelope version)
    if (msg.ciphertext.startsWith("{") && msg.ciphertext.includes('"v"')) {
      return "🔒 Verschlüsselte Nachricht";
    }
    // Also check contentType — if it's e2ee or e2ee_group, never show raw ciphertext
    if (msg.contentType === "e2ee" || msg.contentType === "e2ee_group") {
      return "🔒 Verschlüsselte Nachricht";
    }
    // Legacy non-encrypted messages: ciphertext field IS the plaintext
    return msg.ciphertext;
  }

  return fallback;
}

/** Convenience: safeMessageText + humanizeFileTokens + stripMarkdown for sidebar previews */
export function safePreviewText(msg: Parameters<typeof safeMessageText>[0], fallback = ""): string {
  return stripMarkdownForPreview(humanizeFileTokens(safeMessageText(msg, fallback)));
}
